'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["itemId", "label"];
import * as React from 'react';
import { styled } from '@mui/material/styles';
import { checkboxClasses } from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { useStore } from '@base-ui/utils/store';
import { useStableCallback } from '@base-ui/utils/useStableCallback';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-internals/use-event-calendar-store-context';
import { schedulerResourceSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { TreeItem, treeItemClasses } from '@mui/x-tree-view/TreeItem';
import { useRichTreeViewApiRef } from '@mui/x-tree-view/hooks';
import clsx from 'clsx';
import { getPaletteVariants } from "../../internals/utils/tokens.mjs";
import { useEventCalendarStyledContext } from "../EventCalendarStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ResourcesTreeRoot = styled('section', {
  name: 'MuiEventCalendar',
  slot: 'ResourcesTree'
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1),
  overflowY: 'auto',
  scrollbarWidth: 'thin',
  [`&[data-flat] .${treeItemClasses.iconContainer}`]: {
    display: 'none'
  }
}));
const ResourcesTreeLabel = styled(Typography, {
  name: 'MuiEventCalendar',
  slot: 'ResourcesTreeLabel'
})(({
  theme
}) => _extends({}, theme.typography.subtitle2, {
  fontWeight: theme.typography.fontWeightMedium,
  paddingLeft: theme.spacing(1.25),
  paddingBottom: theme.spacing(1)
}));
const ResourcesTreeItemRoot = styled(TreeItem, {
  name: 'MuiEventCalendar',
  slot: 'ResourcesTreeItem'
})(({
  theme
}) => ({
  [`& .${treeItemClasses.content}[data-selected]`]: {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: (theme.vars || theme).palette.action.hover,
      '@media (hover: none)': {
        backgroundColor: 'transparent'
      }
    },
    '&[data-focused]': {
      backgroundColor: (theme.vars || theme).palette.action.focus
    }
  },
  [`& .${checkboxClasses.root}`]: {
    color: 'var(--event-main)',
    [`&.${checkboxClasses.checked}`]: {
      color: 'var(--event-main)'
    }
  },
  variants: getPaletteVariants(theme)
}));
const ResourcesTreeItemLabel = styled(Typography, {
  name: 'MuiEventCalendar',
  slot: 'ResourcesTreeItemLabel'
})(({
  theme
}) => ({
  fontSize: theme.typography.body2.fontSize
}));
const getItemLabel = item => item.title;
function ResourcesTreeItem(props) {
  const {
      itemId,
      label
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    classes
  } = useEventCalendarStyledContext();
  const store = useEventCalendarStoreContext();
  const eventColor = useStore(store, schedulerResourceSelectors.defaultEventColor, itemId);
  return /*#__PURE__*/_jsx(ResourcesTreeItemRoot, _extends({
    itemId: itemId,
    label: /*#__PURE__*/_jsx(ResourcesTreeItemLabel, {
      className: classes.resourcesTreeItemLabel,
      children: label
    }),
    "data-palette": eventColor,
    className: classes.resourcesTreeItem,
    slotProps: {
      checkbox: {
        className: classes.resourcesTreeItemCheckbox,
        size: 'small',
        slotProps: {
          input: {
            tabIndex: -1
          }
        }
      }
    }
  }, other));
}
export const ResourcesTree = /*#__PURE__*/React.forwardRef(function ResourcesTree(props, forwardedRef) {
  const {
    classes,
    localeText
  } = useEventCalendarStyledContext();
  const store = useEventCalendarStoreContext();
  const apiRef = useRichTreeViewApiRef();
  const headingId = React.useId();
  const resources = useStore(store, schedulerResourceSelectors.processedResourceList);
  const childrenLookup = useStore(store, schedulerResourceSelectors.processedResourceChildrenLookup);
  const flatList = useStore(store, schedulerResourceSelectors.processedResourceFlatList);
  const visibleMap = useStore(store, schedulerResourceSelectors.visibleMap);
  const getItemChildren = React.useCallback(item => childrenLookup.get(item.id) ?? [], [childrenLookup]);
  const selectedItems = React.useMemo(() => {
    // A resource is "fully selected" only if itself and every descendant are visible.
    const fullyVisible = new Set();
    for (let i = flatList.length - 1; i >= 0; i -= 1) {
      const resource = flatList[i];
      if (visibleMap[resource.id] === false) {
        continue;
      }
      const children = childrenLookup.get(resource.id);
      if (!children || children.every(child => fullyVisible.has(child.id))) {
        fullyVisible.add(resource.id);
      }
    }
    return flatList.filter(r => fullyVisible.has(r.id)).map(r => r.id);
  }, [flatList, visibleMap, childrenLookup]);
  const defaultExpandedItems = React.useMemo(() => Array.from(childrenLookup.keys()), [childrenLookup]);
  const handleSelectedItemsChange = useStableCallback((event, newSelectedIds) => {
    const newSelected = new Set(newSelectedIds);
    const newVisibleResources = {};
    // Walk in reverse (leaves before parents) so a parent is marked visible when any descendant is selected, even if the parent itself was removed.
    for (let i = flatList.length - 1; i >= 0; i -= 1) {
      const resource = flatList[i];
      if (newSelected.has(resource.id)) {
        newVisibleResources[resource.id] = true;
        continue;
      }
      const children = childrenLookup.get(resource.id);
      const hasVisibleDescendant = children?.some(child => newVisibleResources[child.id] === true);
      newVisibleResources[resource.id] = hasVisibleDescendant ?? false;
    }
    store.setVisibleResources(newVisibleResources, event?.nativeEvent);
  });
  const handleItemClick = useStableCallback((event, itemId) => {
    // Clicks already handled by the checkbox or the expand-collapse icon must not double-toggle.
    const target = event.target;
    if (target.closest(`.${treeItemClasses.checkbox}`) || target.closest(`.${treeItemClasses.iconContainer}`)) {
      return;
    }
    apiRef.current?.setItemSelection({
      event,
      itemId,
      keepExistingSelection: true
    });
  });
  if (resources.length === 0) {
    return null;
  }
  const isFlat = childrenLookup.size === 0;
  return /*#__PURE__*/_jsxs(ResourcesTreeRoot, _extends({
    ref: forwardedRef
  }, props, {
    className: clsx(props.className, classes.resourcesTree),
    "data-flat": isFlat || undefined,
    children: [/*#__PURE__*/_jsx(ResourcesTreeLabel, {
      id: headingId,
      className: classes.resourcesTreeLabel,
      children: localeText.resourcesLabel
    }), /*#__PURE__*/_jsx(RichTreeView, {
      items: resources,
      apiRef: apiRef,
      getItemChildren: getItemChildren,
      getItemLabel: getItemLabel,
      multiSelect: true,
      checkboxSelection: true,
      selectionPropagation: {
        parents: true,
        descendants: true
      },
      expansionTrigger: "iconContainer",
      selectedItems: selectedItems,
      defaultExpandedItems: defaultExpandedItems,
      onSelectedItemsChange: handleSelectedItemsChange,
      onItemClick: handleItemClick,
      slots: {
        item: ResourcesTreeItem
      },
      "aria-labelledby": headingId
    })]
  }));
});
if (process.env.NODE_ENV !== "production") ResourcesTree.displayName = "ResourcesTree";